/**
 * Armour and clothing — rare, heavy, and local to the parts it actually covers.
 *
 * Two rules do all the work here, and both come straight from AoD (§4.3/§9.2).
 *
 * **Coverage or nothing.** A helm is worth three against a blow to the head and
 * *nothing at all* against a blow to the neck a hand's breadth below it. That is
 * what makes armour a set of decisions rather than a subtraction, and it is why
 * `armourValueAt` takes a zone.
 *
 * **The material answers the blow, not the number on the label** (M2-04's
 * addition, AoD §4.4's signature behaviours). Mail is a cut-stopper: four rings
 * deep will turn a seax slash all day, and does very little about the axe-haft
 * that lands on top of it, because the blow transmits through the links into the
 * ribs underneath. A spear point is in between — it spreads the rings and gets
 * partway in, which is a large part of why the spear outlived the sword in this
 * century. Leather is the reverse: it is padding, so it is better against the
 * hafts than against the edges. Those behaviours are the `against` maps below,
 * and between them they are why the shipped kit has no single best answer.
 *
 * In sixth-century Britain mail is a nobleman's inheritance, not a uniform.
 * Most people in Annwn will fight in wool, and the wool is in this file too —
 * with no armour value at all — because the log has to be able to say what the
 * blade opened before it reached the man.
 */
/* -------------------------------------------------------------------------- *
 * The clothing layer — no armour value, and load-bearing anyway
 * -------------------------------------------------------------------------- */
/**
 * What everybody in the valley is wearing. Worth nothing, and in the kit
 * regardless: *"the blade goes through his tunic and into the shoulder"* is a
 * line the narrator can only write if the sim knows about the tunic.
 */
export const TUNIC = {
    id: 'tunic',
    name: 'a wool tunic',
    description: 'Undyed wool, woven at somebody’s own loom and mended at the elbows. It ' +
        'keeps the weather out. It will not keep anything else out.',
    covers: ['chest', 'abdomen', 'arm', 'leg'],
    armourValue: 0,
};
/**
 * A heavy wool cloak, doubled over the shoulder. Not armour, quite — but a
 * thick fold of wet wool has turned more knives than anybody likes to admit,
 * and it is worthless against a point.
 */
export const CLOAK = {
    id: 'cloak',
    name: 'a wool cloak',
    description: 'Four ells of coarse wool, pinned at the shoulder with an iron ring-brooch ' +
        'and greasy enough to shed rain. Doubled over the arm it is worth ' +
        'something. Not much, and not against a spear.',
    covers: ['chest', 'arm'],
    armourValue: 1,
    against: { puncture: 0.5 },
};
/* -------------------------------------------------------------------------- *
 * Armour proper
 * -------------------------------------------------------------------------- */
/** Boiled leather over the body. Common enough that a farmer might own one. */
export const JERKIN = {
    id: 'jerkin',
    name: 'a leather jerkin',
    description: 'Ox-hide, boiled hard and stitched in overlapping plates across the chest. ' +
        'A season’s wages, and the most armour most people will ever own.',
    covers: ['chest', 'abdomen', 'arm'],
    armourValue: 2,
    against: { blunt: 0.5, puncture: 0.75 },
};
/** A stiffened cap of hide. What a farmer wears when the alternative is nothing. */
export const HIDE_CAP = {
    id: 'hide-cap',
    name: 'a hide cap',
    description: 'Layered hide sewn over a felt liner, tied under the chin. It is not a helm ' +
        'and everyone who owns one knows it, but a skull is a skull.',
    covers: ['head'],
    armourValue: 1,
    against: { puncture: 0.5 },
};
/**
 * A mail shirt. A war-band's wealth, and it shows in the wound tables — and in
 * the wind of the man wearing it, who is spending half again the breath his
 * opponent is on every swing.
 */
export const BYRNIE = {
    id: 'byrnie',
    name: 'a mail byrnie',
    description: 'Some thousands of riveted rings, hip-length and split for riding, worn ' +
        'over a padded jerkin. There is one in the commote. Everybody knows whose ' +
        'it is, and what it is worth, and what happened to the man it came from.',
    covers: ['chest', 'abdomen', 'arm'],
    armourValue: 4,
    // The whole point of mail, and the whole limit of it.
    against: { blunt: 0.25, puncture: 0.5 },
    poolPenalty: 2,
};
export const HELM = {
    id: 'helm',
    name: 'an iron helm',
    description: 'An iron cap over a frame of bands, with a nasal that has been straightened ' +
        'more than once. Worth more than a cow and cheaper than a head.',
    covers: ['head'],
    armourValue: 3,
    against: { blunt: 0.75 },
};
/** Greaves. Rarer than the helm, and the reason a spearman's shins survive. */
export const GREAVES = {
    id: 'greaves',
    name: 'leather greaves',
    description: 'Hardened hide strapped over the shin from knee to ankle. Everything in a ' +
        'shield-wall that is not aimed at your face is aimed at your legs.',
    covers: ['leg', 'foot'],
    armourValue: 2,
    against: { blunt: 0.5, puncture: 0.75 },
};
/* -------------------------------------------------------------------------- *
 * Natural armour — what an animal is wearing because of what it is
 * -------------------------------------------------------------------------- */
/**
 * A boar's shield-skin: matted bristle over a slab of gristle the animal grows
 * across its own shoulders by rubbing them against pine resin all autumn.
 *
 * The most important two lines of content in M2-05. Two points over the chest
 * and shoulders, and *better than that against an edge* — a cut at a boar's
 * body is very nearly a wasted swing, and a hunter who has understood that
 * aims at the head or hamstrings the legs. Which is M2-03's aimed blows
 * earning their keep against something that is not a person.
 */
export const BOAR_HIDE = {
    id: 'boar.hide',
    name: 'its hide',
    description: 'Bristle over a hand-thick plate of gristle across the shoulders, ' +
        'lacquered with pine resin and scarred over more than once. Boars grow it ' +
        'on purpose, against each other’s tusks.',
    covers: ['chest', 'abdomen'],
    armourValue: 2,
    // Slashing at it is close to useless; a point still finds a way through.
    against: { cut: 1.5, puncture: 0.5 },
};
/**
 * Ysgithyrwyn's hide (M5-04). The boar's shield-skin, grown a lifetime longer.
 *
 * Three points where a wild boar has two, and over the head as well as the
 * chest — the armoured front he presents to anything standing its ground. It is
 * the whole reason the fight is what it is: a spear into his shoulders is a
 * spear into a hand's depth of scarred gristle, and a party that has not learned
 * that hammers his front all afternoon and buries its dead.
 *
 * What it does *not* cover is the lesson: his flanks and his belly are bare, so
 * the hunt is a problem of getting to his side — and his legs and his eyes are
 * bare too, which is M2-03's aimed blow finding the one place the animal cannot
 * armour. Bringing him down off his legs is what opens the belly, because a boar
 * on the ground can no longer keep it under him.
 */
export const YSGITHYRWYN_HIDE = {
    id: 'ysgithyrwyn.hide',
    name: 'his hide',
    description: 'A hand and a half of resin-black gristle across the shoulders and the ' +
        'brow, cured hard by more winters than any man in the valley has seen, and ' +
        'starred all over with the pale knots of old spear-wounds that never quite ' +
        'closed.',
    // The front and the head he leads with — not the flanks, and not the belly.
    covers: ['chest', 'head'],
    armourValue: 3,
    // An edge does nothing to it at all; a point, driven with a man's whole weight
    // behind a couched spear, still occasionally tells.
    against: { cut: 2, puncture: 0.6 },
};
/** A stag's winter coat. Barely armour, and the log should still name it. */
export const STAG_HIDE = {
    id: 'stag.hide',
    name: 'its winter coat',
    description: 'Dense red-brown hair over a thin hide, thickest along the spine.',
    covers: ['chest', 'abdomen'],
    armourValue: 1,
    against: { puncture: 0.5 },
};
/* -------------------------------------------------------------------------- *
 * Shields — armour that carries dice, and wears out
 * -------------------------------------------------------------------------- */
/**
 * A limewood round shield. Armour that carries defence dice (M2-02): it is in
 * this list rather than in a slot of its own because "what am I wearing" should
 * have one answer, and because a shield genuinely does cover the arm behind it.
 *
 * Two dice against anything from the front or the flank, and nothing at all from
 * behind — which is most of what makes flanking worth the walk. And it is
 * **consumable**: `SHIELD_CONDITIONS` in the sim takes it from sound to notched
 * to split to firewood under sustained blows, and the dice go with it.
 */
export const SHIELD = {
    id: 'shield',
    name: 'a limewood shield',
    description: 'Lime boards glued edge to edge, faced with hide and bossed with iron, ' +
        'about a yard across. It is a board. Boards give way, and this one has ' +
        'already been re-rimmed twice.',
    covers: ['arm'],
    armourValue: 2,
    defenceDice: 2,
    poolPenalty: 1,
};
/** The lighter answer: a fist-shield that covers the hand and not much else. */
export const BUCKLER = {
    id: 'buckler',
    name: 'a buckler',
    description: 'An iron boss the size of a spread hand with a scrap of board behind it, ' +
        'held in the fist rather than strapped to the arm. It costs you nothing to ' +
        'carry and it covers nothing you are not actively holding it in front of.',
    covers: ['hand'],
    armourValue: 1,
    defenceDice: 1,
};
const ARMOURY = [
    TUNIC,
    CLOAK,
    JERKIN,
    HIDE_CAP,
    BYRNIE,
    HELM,
    GREAVES,
    SHIELD,
    BUCKLER,
    BOAR_HIDE,
    YSGITHYRWYN_HIDE,
    STAG_HIDE,
];
export const ARMOUR_KIT = new Map(ARMOURY.map((piece) => [piece.id, piece]));
export function armour(id) {
    const piece = ARMOUR_KIT.get(id);
    if (!piece)
        throw new Error(`unknown armour '${id}'`);
    return piece;
}
//# sourceMappingURL=armour.js.map